// Everything drawn on the map, as Phaser objects. Two kinds of coordinates:
// - world: map pixels (a tile is 16), seen through the camera's zoom: the sea, the deck, otters and their shadows;
// - screen: CSS pixels, for what keeps its size at any zoom (names, bubbles, signs, the hearing ring...). These live in
//   "screen layers": objects placed at -cam.x/zoom with scale 1/zoom, so their own coordinates are screen pixels.
//   Name tags sit over everything in the world (otters, tables, desks, the mast), in the otters' order among themselves.
// The maths is the deck's original 2D-canvas drawing's, line for line (cam.x + tiles * S on screen, S = 16 * zoom).
import { buildSheet, buildActSheet, buildDuoSheet } from '/looks.js';
import { otterFrame } from './pose.js';
import { HEART_ICON, EMOTE_MS, iconOf } from '/emotes.js';
import { measure, textStyle, placeText, fontMetrics, bubbleLines, BUBBLE_FONT } from './text.js';
import { INK, YELLOW, CREAM, SIGN_TEXT_ON, SIGNS, segText, isSign, css, roomFont, signFont, roomPlaque, hutSign, unstackSigns } from './labels.js';
import { FISHING_GEO } from './fishing-geo.js';
import { t as tx } from '/i18n.js';
import { FRAMES as UI_FRAMES, COLS as UI_COLS } from '/icons/ui-icons.js';

/** The UI's pixel icons (public/static/icons/ui-icons.png, loaded by deck/scene.js as the 'ui-icons' spritesheet): an image
 *  showing icon `name`, top-left origin, drawn at whole multiples of 16 so it stays crisp. */
const UI = 'ui-icons';
const uiFrame = (name) => { const f = UI_FRAMES[name] ?? UI_FRAMES.exclaim; return f[1] * UI_COLS + f[0]; };
const uiImage = (scene) => scene.add.image(0, 0, scene.textures.exists(UI) ? UI : '__DEFAULT', 0).setOrigin(0, 0).setVisible(false);
const showIcon = (img, name, x, y, size = 16) => (img.scene.textures.exists(UI) ? img.setTexture(UI, uiFrame(name)).setPosition(Math.round(x), Math.round(y)).setScale(size / 16).setVisible(true) : img.setVisible(false));
const TAG_ICON = 17; // a name tag's icon and the gap after it

const NAME_FONT = '12px system-ui', RALLY_FONT = 'bold 12px system-ui', TABLE_FONT = 'bold 12px system-ui';
const ROOM0 = roomFont(3), SIGN0 = signFont(3); // the labels' first fonts (the same at the 2x a computer starts at), restyled as the zoom moves
/** The 五子棋 tables: the table's wood, the board on it, its grid, the stools (the sign is CREAM, from labels.js). */
const TABLE_WOOD = 0xc98a4b, TABLE_BOARD = 0xe8bd6f, TABLE_GRID = 0x6b4a1f, STOOL = 0x8b5a2b, STONE_WHITE = 0xfffdf6;
export const HEAR_RADIUS = 5;
// zones (room plaques, hut and 码头 signs): over everything on the deck (the fronts, otters: a lifebuoy on the 活动舱's rail
// was drawn over the end of a hut's sign), under the name tags and speech bubbles
export const DEPTH = { waves: 0, map: 1, range: 3, rally: 4, fishing: 6, otters: 100, zones: 4e5, tags: 5e5, bubbles: 1e6 };
/** Y-sorting: an otter at y (its sprite's top-left tile) is at DEPTH.otters + y, its feet at y + 0.8 (seats.js). What
 *  stands on the deck goes in the same order by where it meets the deck: groundDepth(y) for ground line y (tiles), so
 *  an otter whose feet are above (behind) that line is drawn under it and one in front over it. */
export const FEET = 0.8;
export const groundDepth = (y) => DEPTH.otters + y - FEET;
/** A table's ground line, in tiles below its point: half way down its top, just above the players' feet at the stools
 *  (feet at t.y + 0.5, seats.js), so the players sit over its ends while anyone behind it is hidden by it (and its
 *  board and stones) instead of standing on the board. */
const TABLE_GROUND = 0.45;

/** Make a screen layer follow the camera (see top): its children are placed in CSS pixels (deck/camera.js); on a
 *  HiDPI canvas the Phaser camera's zoom carries the render scale too (deck/scene.js, deck/dpr.ts), so they come out
 *  d canvas pixels per CSS pixel and Text is drawn at that resolution. */
export const toScreen = (o, cam) => o.setPosition(-cam.x / cam.scale, -cam.y / cam.scale).setScale(1 / cam.scale);

// Phaser's Graphics draws every arc with 100 points, however small; these make points about every 3 px instead.
function arcPoints(cx, cy, r, a0, a1, out = []) {
  const n = Math.max(2, Math.ceil((Math.abs(a1 - a0) * r) / 3));
  for (let i = 0; i <= n; i++) { const a = a0 + ((a1 - a0) * i) / n; out.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r }); }
  return out;
}
function roundedRectPoints(x, y, w, h, r) {
  const P = Math.PI, out = [];
  arcPoints(x + w - r, y + r, r, -P / 2, 0, out); arcPoints(x + w - r, y + h - r, r, 0, P / 2, out);
  arcPoints(x + r, y + h - r, r, P / 2, P, out); arcPoints(x + r, y + r, r, P, 1.5 * P, out);
  return out;
}
/** Colour a white quad. WebGL tints it; the Canvas renderer (no WebGL) can't tint, so there it shows a texture of that
 *  colour instead, made once per colour. Call before setDisplaySize (the size is in the texture's pixels). The texture
 *  is big because, with pixelArt, Phaser's Canvas renderer draws every image half a texture pixel too wide and tall
 *  (to hide seams): at 256 px that is a fraction of a screen pixel on any quad here. */
function paint(img, color) {
  const scene = img.scene;
  if (scene.sys.renderer.type !== 1) return img.setTint(color); // 1 = Phaser.CANVAS
  const key = 'solid:' + color;
  if (!scene.textures.exists(key)) {
    const c = document.createElement('canvas'); c.width = c.height = 256;
    const g = c.getContext('2d'); g.fillStyle = '#' + color.toString(16).padStart(6, '0'); g.fillRect(0, 0, 256, 256);
    scene.textures.addCanvas(key, c);
  }
  return img.setTexture(key);
}
/** Rectangles and straight lines as tinted white quads, reused frame to frame. Unlike a Graphics they batch with
 *  the sprites and text around them, so a layer of them costs about one draw call. */
class Quads {
  constructor(scene, parent) { this.scene = scene; this.box = scene.add.container(0, 0); parent.add(this.box); this.pool = []; this.n = 0; }
  begin() { this.n = 0; return this; }
  next() {
    let q = this.pool[this.n++];
    if (!q) { q = this.scene.add.image(0, 0, '__WHITE'); this.box.add(q); this.pool.push(q); }
    return q.setVisible(true);
  }
  rect(x, y, w, h, color, alpha = 1) { paint(this.next(), color).setOrigin(0, 0).setRotation(0).setPosition(x, y).setDisplaySize(w, h).setAlpha(alpha); }
  /** like canvas strokeRect: a line of width lw centred on the edge */
  strokeRect(x, y, w, h, lw, color, alpha = 1) {
    const k = lw / 2;
    this.rect(x - k, y - k, w + lw, lw, color, alpha); this.rect(x - k, y + h - k, w + lw, lw, color, alpha);
    this.rect(x - k, y + k, lw, h - lw, color, alpha); this.rect(x + w - k, y + k, lw, h - lw, color, alpha);
  }
  /** a straight line with butt ends */
  line(x1, y1, x2, y2, lw, color, alpha = 1) {
    paint(this.next(), color).setOrigin(0, 0.5).setPosition(x1, y1).setDisplaySize(Math.hypot(x2 - x1, y2 - y1), lw).setRotation(Math.atan2(y2 - y1, x2 - x1)).setAlpha(alpha);
  }
  end() { for (let i = this.n; i < this.pool.length; i++) this.pool[i].setVisible(false); }
}

/** Dashes along a polyline, like canvas setLineDash([dash, gap]) with lineDashOffset `offset`; seg(x1, y1, x2, y2) each. */
function dashedPolyline(seg, pts, dash, gap, offset = 0) {
  const period = dash + gap; let s = ((-offset % period) + period) % period; // distance along the pattern
  for (let i = 0; i + 1 < pts.length; i++) {
    const [ax, ay] = pts[i], [bx, by] = pts[i + 1], len = Math.hypot(bx - ax, by - ay);
    let t = 0;
    while (t < len) {
      const on = s < dash, run = Math.min(len - t, on ? dash - s : period - s);
      if (on) seg(ax + ((bx - ax) * t) / len, ay + ((by - ay) * t) / len, ax + ((bx - ax) * (t + run)) / len, ay + ((by - ay) * (t + run)) / len);
      t += run; s = (s + run) % period;
    }
  }
}
/** A dashed circle as canvas strokes arc(cx, cy, r, 0, 7) with setLineDash([dash, gap]) and lineDashOffset `offset`
 *  (an arc of more than a full turn is exactly one turn, starting at angle 0). Drawn as straight pieces of at most
 *  10 px, within 0.2 px of the arc on any ring 80 px or more across. */
function dashedCircle(seg, cx, cy, r, dash, gap, offset) {
  const period = dash + gap, total = 2 * Math.PI * r;
  let s = ((-offset % period) + period) % period, d = 0;
  while (d < total) {
    const on = s < dash, run = Math.min(total - d, on ? dash - s : period - s);
    if (on) {
      const a = d / r, b = (d + run) / r, n = Math.max(1, Math.ceil(run / 10));
      for (let i = 0; i < n; i++) { const p = a + ((b - a) * i) / n, q = a + ((b - a) * (i + 1)) / n; seg(cx + Math.cos(p) * r, cy + Math.sin(p) * r, cx + Math.cos(q) * r, cy + Math.sin(q) * r); }
    }
    d += run; s = (s + run) % period;
  }
}

/** The open sea's drifting waves (world): 260 little dashes, as one Blitter. */
export class Waves {
  constructor(scene, MAP, seaMargin) {
    if (!scene.textures.exists('wave')) { const c = document.createElement('canvas'); c.width = 5; c.height = 1; const g = c.getContext('2d', { willReadFrequently: true }); g.fillStyle = '#5fb6e6'; g.fillRect(0, 0, 5, 1); scene.textures.addCanvas('wave', c); }
    this.blitter = scene.add.blitter(0, 0, 'wave').setDepth(DEPTH.waves);
    const M = seaMargin;
    this.bobs = Array.from({ length: 260 }, (_, i) => this.blitter.create(0, ((i * 61) % ((MAP.height + 2 * M) * 16)) - M * 16));
    this.MAP = MAP; this.M = M;
  }
  /** view: the world rectangle on screen (only the waves in it are drawn) */
  update(now, view) {
    const { MAP, M } = this, x0 = view.x - 5, x1 = view.right, y0 = view.y - 1, y1 = view.bottom;
    this.bobs.forEach((b, i) => {
      // (round the map as it is now: a grown one starts at x0, y0)
      b.x = ((i * 97) % ((MAP.width + 2 * M) * 16)) + ((MAP.x0 ?? 0) - M) * 16 + ((now / 90 + i * 13) % 48) - 24;
      b.y = ((i * 61) % ((MAP.height + 2 * M) * 16)) + ((MAP.y0 ?? 0) - M) * 16;
      b.visible = b.x > x0 && b.x < x1 && b.y > y0 && b.y < y1;
    });
  }
}

/** Room names on their plaques, and the blinking signs over the two game huts (screen); deck/labels.js says where. */
export class ZoneLabels {
  constructor(scene, zones) {
    this.layer = scene.add.container(0, 0).setDepth(DEPTH.zones);
    this.q = new Quads(scene, this.layer);
    this.texts = scene.add.container(0, 0); this.layer.add(this.texts); // over the quads
    this.items = zones.map((z) => {
      // one Text per look, restyled only when the zoom moves its font to another size
      if (!isSign(z)) { const t = scene.add.text(0, 0, z.name, textStyle(ROOM0, css(INK))); this.texts.add(t); return { z, t, font: ROOM0 }; }
      // a sign: its words (one Text per look each) and its icons, in order (deck/labels.js SIGNS)
      const parts = SIGNS[z.id].map((s) => {
        const w = segText(s);
        if (w === null) { const img = uiImage(scene); this.texts.add(img); return { img }; }
        const on = scene.add.text(0, 0, w, textStyle(SIGN0, css(SIGN_TEXT_ON))), off = scene.add.text(0, 0, w, textStyle(SIGN0, css(INK)));
        this.texts.add([on, off]); return { on, off };
      });
      return { z, sign: true, parts, font: SIGN0 };
    });
    this.drawn = []; // what was drawn last frame (deck/labels.js layouts), for debugging
  }
  /** tag(zoneId): a few words after a room's name on its plaque, or null (日历: 「会议中」, 「15:00 有会」; public/calendar.js). */
  update(now, cam, tag) {
    toScreen(this.layer, cam);
    const q = this.q.begin(), drawn = this.drawn; drawn.length = 0;
    // the layouts first: plaques where their rooms are, then the hut signs, stacked so none runs into another (or a
    // plaque) when zoomed out (labels.js unstackSigns); then all the boxes, then the text over them (one container
    // each), so a plaque never hides a sign's text
    const lay = this.items.map((it) => {
      if (it.sign) return hutSign(it.z, cam, now);
      const extra = tag?.(it.z.id), name = extra ? `${it.z.name} · ${extra}` : it.z.name;
      if (name !== it.t.text) { it.t.setText(name); } // (the name follows the language: deck/places.js)
      if (name === it.z.name) it.zn = null; else if (it.zn?.name !== name) it.zn = { ...it.z, name };
      return roomPlaque(it.zn ?? it.z, cam);
    });
    unstackSigns(lay.filter((L, i) => this.items[i].sign), lay.filter((L, i) => !this.items[i].sign));
    this.items.forEach((it, i) => {
      const L = lay[i]; drawn.push(L);
      if (!it.sign) {
        if (L.font !== it.font) { it.t.setStyle(textStyle(L.font, css(INK))); it.font = L.font; }
        for (const r of L.rects) q.rect(...r);
        placeText(it.t, L.x, L.baseline, L.font);
        return;
      }
      const restyle = L.font !== it.font; it.font = L.font;
      q.rect(...L.fill);
      q.strokeRect(L.box.x, L.box.y, L.box.w, L.box.h, 2, INK);
      it.parts.forEach((p, j) => {
        const at = L.parts[j];
        if (p.img) { showIcon(p.img, at.icon, at.x, at.y, at.size); return; }
        if (restyle) { p.on.setStyle(textStyle(L.font, css(SIGN_TEXT_ON))); p.off.setStyle(textStyle(L.font, css(INK))); }
        if (p.on.text !== at.text) { p.on.setText(at.text); p.off.setText(at.text); } // (a catalog text: a language change)
        p.on.setVisible(L.on); p.off.setVisible(!L.on);
        placeText(L.on ? p.on : p.off, at.x, L.baseline, L.font);
      });
    });
    for (const L of drawn) if (L.lights) for (const r of L.lights) q.rect(...r);
    q.end();
  }
}

/** Who can hear me: my holt lit up, or a ring of HEAR_RADIUS tiles on the open deck (screen). */
export class Range {
  constructor(scene) {
    this.layer = scene.add.container(0, 0).setDepth(DEPTH.range);
    this.g = scene.add.graphics(); this.layer.add(this.g); // the disc
    this.q = new Quads(scene, this.layer); // the room's fill, and the dashes
  }
  update(now, cam, me, zone, talk) {
    const g = this.g, q = this.q.begin(); toScreen(this.layer, cam);
    if (me) {
      const S = cam.S();
      if (zone) {
        g.clear().setVisible(false);
        const x = cam.x + zone.x * S, y = cam.y + zone.y * S, w = zone.w * S, h = zone.h * S;
        q.rect(x, y, w, h, YELLOW, talk ? 0.28 : 0.14);
        dashedPolyline((x1, y1, x2, y2) => q.line(x1, y1, x2, y2, 3, YELLOW, 0.9), [[x, y], [x + w, y], [x + w, y + h], [x, y + h], [x, y]], 8, 6);
      } else {
        const cx = cam.x + (me.x + 0.5) * S, cy = cam.y + (me.y + 0.8) * S, r = HEAR_RADIUS * S;
        g.clear().setVisible(true).fillStyle(YELLOW, talk ? 0.22 : 0.1).fillCircle(cx, cy, r);
        const a = talk ? 0.95 : 0.55;
        dashedCircle((x1, y1, x2, y2) => q.line(x1, y1, x2, y2, 3, YELLOW, a), cx, cy, r, 10, 8, -now / 60);
      }
    }
    q.end();
  }
}

/** The gather flag (screen). */
export class Rally {
  constructor(scene) {
    this.layer = scene.add.container(0, 0).setDepth(DEPTH.rally);
    this.g = scene.add.graphics(); this.t = scene.add.text(0, 0, '', textStyle(RALLY_FONT, '#1b1410'));
    this.layer.add([this.g, this.t]);
  }
  update(now, cam, rally) {
    this.layer.setVisible(!!rally); if (!rally) return;
    toScreen(this.layer, cam);
    const S = cam.S(), SCALE = cam.scale, g = this.g; g.clear();
    const fx = cam.x + (rally.x + 0.5) * S, fy = cam.y + (rally.y + 0.9) * S, wave = Math.sin(now / 200) * 2 * SCALE;
    g.lineStyle(2 * SCALE, 0x3b2416, 1).lineBetween(fx, fy, fx, fy - 26 * SCALE);
    g.fillStyle(YELLOW, 1).fillTriangle(fx, fy - 26 * SCALE, fx + 16 * SCALE, fy - 21 * SCALE + wave, fx, fy - 15 * SCALE);
    g.lineStyle(SCALE, 0x3b2416, 1).strokeTriangle(fx, fy - 26 * SCALE, fx + 16 * SCALE, fy - 21 * SCALE + wave, fx, fy - 15 * SCALE);
    const label = tx('map.rally', { name: rally.by }); if (this.t.text !== label) this.t.setText(label);
    placeText(this.t, fx - measure(RALLY_FONT, label) / 2, fy - 30 * SCALE, RALLY_FONT);
  }
}

/** The game tables (五子棋 and the other board games; screen): a little table with a board on it and a stool each side, and a sign over it; the sign
 *  turns yellow (⚫⚪ 对局中) while a game is on there. Tables come from the map data; whether busy, from the office. */
export class Tables {
  constructor(scene, tables) {
    // Each table (top, board, stones, stools) is its own layer, y-sorted with the otters (TABLE_GROUND).
    // Each sign sorts with the otters a few rows up: whoever stands just above a table (watching) covers its sign
    // rather than wearing it; the players at the stools are clear of it anyway.
    this.items = tables.map((t) => {
      const signs = scene.add.container(0, 0).setDepth(DEPTH.otters + t.y - 3), txt = scene.add.text(0, 0, '', textStyle(TABLE_FONT, '#1b1410'));
      const sq = new Quads(scene, signs), ic = uiImage(scene); signs.add([txt, ic]);
      const layer = scene.add.container(0, 0).setDepth(groundDepth(t.y + TABLE_GROUND));
      return { t, txt, ic, signs, sq, layer, q: new Quads(scene, layer) };
    });
  }
  /** list: [{ id, x, y, busy, seated, kindName }] */
  update(now, cam, list) {
    const S = cam.S(), k = cam.scale, lw = Math.max(1.5, k);
    for (const it of this.items) {
      toScreen(it.signs, cam); toScreen(it.layer, cam);
      const q = it.q.begin();
      const sq = it.sq.begin();
      const t = list?.find((x) => x.id === it.t.id) ?? it.t;
      const x0 = cam.x + (t.x - 0.4) * S, y0 = cam.y + (t.y + 0.05) * S, w = 1.8 * S, h = 0.95 * S;
      for (const sx of [t.x - 1.25, t.x + 1.75]) { // stools, where the players' feet go (public/gomoku.js)
        const x = cam.x + sx * S, y = cam.y + (t.y + 0.3) * S, d = 0.5 * S;
        q.rect(x + k, y + 2 * k, d, d, INK, 0.25); q.rect(x, y, d, d, STOOL); q.strokeRect(x, y, d, d, lw, INK);
      }
      q.rect(x0 + 2 * k, y0 + 3 * k, w, h, INK, 0.25); // shadow
      q.rect(x0 + 2 * k, y0 + h, 2 * k, 3 * k, STOOL); q.rect(x0 + w - 4 * k, y0 + h, 2 * k, 3 * k, STOOL); // legs
      q.rect(x0, y0, w, h, TABLE_WOOD); q.strokeRect(x0, y0, w, h, lw, INK);
      const bx = x0 + 0.22 * S, by = y0 + 0.1 * S, bw = w - 0.44 * S, bh = h - 0.2 * S;
      q.rect(bx, by, bw, bh, TABLE_BOARD);
      for (let i = 1; i < 5; i++) {
        q.line(bx + (bw * i) / 5, by, bx + (bw * i) / 5, by + bh, Math.max(1, k / 2), TABLE_GRID, 0.7);
        q.line(bx, by + (bh * i) / 5, bx + bw, by + (bh * i) / 5, Math.max(1, k / 2), TABLE_GRID, 0.7);
      }
      if (t.busy) { const d = Math.max(3, 2.2 * k); q.rect(bx + (bw * 2) / 5 - d / 2, by + (bh * 2) / 5 - d / 2, d, d, INK); q.rect(bx + (bw * 3) / 5 - d / 2, by + (bh * 3) / 5 - d / 2, d, d, STONE_WHITE); q.strokeRect(bx + (bw * 3) / 5 - d / 2, by + (bh * 3) / 5 - d / 2, d, d, 1, INK); }
      // the sign
      const game = t.kindName ?? tx('map.table.gomoku'), label = t.busy ? tx('map.table.busy', { game }) : t.seated === 1 ? tx('map.table.waiting', { game }) : game; // busy: the stones icon in front
      if (it.txt.text !== label) it.txt.setText(label);
      const iw = t.busy ? TAG_ICON : 0, tw = measure(TABLE_FONT, label), pw = tw + 12 + iw, ph = 18, cx = cam.x + (t.x + 0.5) * S;
      // just over the table; with someone seated, above their name tags (see OtterView: a player stands at y = t.y - 0.3)
      const bob = t.busy ? Math.round(Math.sin(now / 300) * 1.5) : 0;
      const top = Math.round((t.seated ? cam.y + (t.y - 0.3 + 1) * S - 30 * k - 18 - 6 : y0 - 6) - ph + bob);
      sq.rect(cx - pw / 2, top, pw, ph, t.busy ? YELLOW : CREAM, t.busy ? 1 : 0.85);
      sq.strokeRect(cx - pw / 2, top, pw, ph, 2, INK, t.busy ? 1 : 0.6);
      it.txt.setAlpha(t.busy ? 1 : 0.8);
      placeText(it.txt, cx - tw / 2 + iw / 2, top + 13, TABLE_FONT);
      if (t.busy) showIcon(it.ic, 'stones', cx - pw / 2 + 5, top + 1).setAlpha(1); else it.ic.setVisible(false);
      sq.end(); q.end();
    }
  }
}

/** Otter sprite sheets, one texture per look (looks.js recolours the template and draws the accessories), made the
 *  first time a look is seen; otters with the same look share it. Frame n is the n-th 32x32 cell, row by row: 0-11
 *  stand + walk, then the motion cells (looks.js ANIM: running, swimming, floating, diving in, climbing out).
 *  A sheet no otter has asked for in UNUSED_MS goes (sweep, after every frame's otters): an otter that changed its look
 *  or left frees its old one, so someone changing looks over and over can't fill the page's memory. Every otter asks
 *  for its sheet on every frame (deck/scene.js), so a sheet that goes is on no sprite. */
const UNUSED_MS = 4000, SWEEP_MS = 2000;
export class Sheets {
  constructor(scene, templates) { this.scene = scene; this.templates = templates; this.used = new Map(); this.swept = 0; }
  /** Free the sheets nobody asked for lately. */
  sweep(now = performance.now()) {
    if (now - this.swept < SWEEP_MS) return;
    this.swept = now;
    for (const [key, at] of this.used) if (now - at > UNUSED_MS) { this.used.delete(key); if (this.scene.textures.exists(key)) this.scene.textures.remove(key); }
  }
  key(look) {
    const key = 'otter:' + JSON.stringify(look), tm = this.scene.textures;
    this.used.set(key, performance.now());
    if (!tm.exists(key)) {
      const c = buildSheet(this.templates, look), tex = tm.addCanvas(key, c);
      for (let i = 0; i < 12 * (c.height / 32); i++) tex.add(i, 0, (i % 12) * 32, Math.floor(i / 12) * 32, 32, 32);
    }
    return key;
  }
  /** The look's act sheet (敲电脑 ... 比心) (looks.js buildActSheet), or null until it's ready. Frame n = act cell n. */
  actKey(look) { return this.#extra('otteract:', look, buildActSheet); }
  /** The look's sheet of the two-person actions (looks.js buildDuoSheet, public/duo.js), or null until it's ready. */
  duoKey(look) { return this.#extra('otterduo:', look, buildDuoSheet); }
  #extra(prefix, look, build) {
    const key = prefix + JSON.stringify(look), tm = this.scene.textures;
    this.used.set(key, performance.now());
    if (!tm.exists(key)) {
      const c = build(look); if (!c) return null;
      const tex = tm.addCanvas(key, c), cols = c.width / 32;
      for (let i = 0; i < cols * (c.height / 32); i++) tex.add(i, 0, (i % cols) * 32, Math.floor(i / cols) * 32, 32, 32);
    }
    return key;
  }
}

/** The emote icons (/emotes.js ICONS): frame i is icon i (16x16), HEART_ICON the little floating heart. */
export function emoteTexture(scene, img) {
  if (!img || scene.textures.exists('emotes')) return;
  const tex = scene.textures.addImage('emotes', img);
  for (let i = 0; i < img.width / 16; i++) tex.add(i, 0, i * 16, 0, 16, 16);
}
/** An emote's icon over the name tag: it pops in (grows past its size and settles) and fades out at the end. */
export const EMOTE_ICON_PX = 32; // 16 px icons at 2x (screen pixels)
function iconScale(t, left) {
  const pop = t < 110 ? (t / 110) * 1.3 : t < 220 ? 1.3 - ((t - 110) / 110) * 0.3 : 1;
  return { k: Math.max(0.05, pop), a: Math.max(0, Math.min(1, left / 250)) };
}

/** Raft avatars on name tags. Drawn into a small round texture when the picture's host allows it (CORS); otherwise
 *  shown as a plain <img> over the map at the same spot (a picture from elsewhere can't go into WebGL). */
export class Faces {
  constructor(scene, canvas) {
    this.scene = scene; this.map = new Map(); this.used = new Set();
    this.dom = document.createElement('div'); this.dom.id = 'faces';
    this.dom.style.cssText = 'position:fixed;left:0;top:0;width:0;height:0;pointer-events:none';
    canvas.after(this.dom); // above the map, under every panel and bar
  }
  get(url) {
    if (!url) return null;
    let f = this.map.get(url);
    if (!f) { f = { ready: false }; this.map.set(url, f); this.load(url, f); }
    return f.ready ? f : null;
  }
  load(url, f) {
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => {
      // 14 CSS px, drawn at 3x so it stays sharp on a HiDPI canvas (deck/dpr.ts); shown smooth at 14x14
      const k = 3, c = document.createElement('canvas'); c.width = c.height = 14 * k;
      const g = c.getContext('2d', { willReadFrequently: true }); g.beginPath(); g.arc(7 * k, 7 * k, 7 * k, 0, 7); g.clip(); g.imageSmoothingEnabled = true; g.drawImage(img, 0, 0, 14 * k, 14 * k);
      f.key = 'face:' + url; this.scene.textures.addCanvas(f.key, c).setFilter(1); f.ready = true; // Phaser.Textures.FilterMode.LINEAR
    };
    img.onerror = () => {
      const d = new Image(); d.onload = () => {
        d.style.cssText = 'position:absolute;left:0;top:0;width:14px;height:14px;border-radius:50%;display:none';
        this.dom.appendChild(d); f.img = d; f.ready = true;
      };
      d.src = url;
    };
    img.src = url;
  }
  /** Per frame: <img> faces not placed this frame are hidden. */
  begin() { this.used.clear(); }
  place(f, x, y) { this.used.add(f); f.img.style.transform = `translate(${x}px,${y}px)`; f.img.style.display = ''; }
  end() { for (const f of this.map.values()) if (f.img && !this.used.has(f)) f.img.style.display = 'none'; }
}

/** The soft shadow under an otter: an 18x6 ellipse drawn smooth at 8x and shown with linear filtering. */
function shadowTexture(scene) {
  if (scene.textures.exists('shadow')) return 'shadow';
  const k = 8, c = document.createElement('canvas'); c.width = 18 * k; c.height = 6 * k;
  const g = c.getContext('2d', { willReadFrequently: true }); g.fillStyle = 'rgba(0,0,0,.18)'; g.beginPath(); g.ellipse(9 * k, 3 * k, 9 * k, 3 * k, 0, 0, 7); g.fill();
  scene.textures.addCanvas('shadow', c).setFilter(1); // Phaser.Textures.FilterMode.LINEAR
  return 'shadow';
}
/** A plain rectangle as a tinted white quad: it batches with the sprites and text around it (a Graphics would not). */
const rect = (scene) => scene.add.image(0, 0, '__WHITE').setOrigin(0, 0);
const setRect = (r, x, y, w, h, color, alpha = 1) => paint(r, color).setPosition(x, y).setDisplaySize(w, h).setAlpha(alpha);

/** One otter: shadow and sprite in the world (walking, running, swimming...: deck/pose.js picks the frame; no shadow
 *  in the water); name tag, face, speaking marks on screen. All quads, so a crowd stays cheap. */
export class OtterView {
  constructor(scene) {
    this.body = scene.add.container(0, 0);
    this.shadow = scene.add.image(0, 0, shadowTexture(scene)).setDisplaySize(18, 6);
    this.sprite = scene.add.image(0, 0, '__DEFAULT').setOrigin(0, 0);
    this.body.add([this.shadow, this.sprite]);
    this.tag = scene.add.container(0, 0);
    this.fill = rect(scene); this.edges = [rect(scene), rect(scene), rect(scene), rect(scene)];
    this.face = scene.add.image(0, 0, '__DEFAULT').setOrigin(0, 0).setVisible(false);
    this.label = scene.add.text(0, 0, '', textStyle(NAME_FONT, '#1b1410'));
    this.ring = scene.add.graphics().setVisible(false);
    this.tag.add([this.fill, ...this.edges, this.face, this.label, this.ring]);
    this.scene = scene; this.icon = null; this.hearts = null; // made the first time this otter emotes
    this.tagIcons = []; // the name tag's pixel icons (o.icons), made as needed
    this.iconHits = []; // where they were drawn last frame: [{name, x, y, w, h}] (screen pixels; tooltips, deck/scene.js tagIconAt)
  }
  /** The emote's icon over the name tag (screen pixels: cx, the tag's top), and 爱心's hearts floating up off the head
   *  (drawn at the otter's own pixel size: SCALE). */
  emoteMarks(em, cx, top, headY, SCALE, now) {
    const has = !!em && this.scene.textures.exists('emotes');
    if (!has) { this.icon?.setVisible(false); this.hearts?.forEach((h) => h.setVisible(false)); return; }
    if (!this.icon) { this.icon = this.scene.add.image(0, 0, 'emotes', 0).setOrigin(0.5, 1); this.tag.add(this.icon); }
    const { k, a } = iconScale(em.t, em.left);
    this.icon.setFrame(iconOf(em.def)).setVisible(true).setPosition(Math.round(cx), Math.round(top - 3)).setScale((EMOTE_ICON_PX / 16) * k).setAlpha(a);
    const love = em.id === 7;
    if (love && !this.hearts) { this.hearts = [0, 1, 2].map(() => this.scene.add.image(0, 0, 'emotes', HEART_ICON)); this.tag.add(this.hearts); }
    this.hearts?.forEach((h, i) => {
      if (!love) return h.setVisible(false);
      const ph = ((em.t + i * 420) % 1260) / 1260; // each heart rises and fades, one after another
      const x = cx + (i - 1) * 7 * SCALE + Math.sin(ph * 6 + i * 2) * 2 * SCALE, y = headY - ph * 14 * SCALE;
      h.setVisible(em.t > i * 420).setPosition(Math.round(x), Math.round(y)).setScale(SCALE).setAlpha(Math.min(a, 1 - ph * ph));
    });
  }
  destroy() { this.body.destroy(); this.tag.destroy(); }
  /** o: {now, cam, sheet (texture key), label, icons (pixel icon names before the name), fill/alpha (badge), face, faces, speaking} */
  update(p, o) {
    const { now, cam } = o, S = cam.S(), SCALE = cam.scale;
    const wx = (sx) => (sx - cam.x) / SCALE, wy = (sy) => (sy - cam.y) / SCALE; // screen -> world pixels
    const x = cam.x + p.drawX * S + S / 2 - 16 * SCALE, y = cam.y + p.drawY * S + S - 30 * SCALE;
    if (o.speaking) p._awakeAt = now; // speaking wakes it (deck/pose.js sleep)
    const fr = otterFrame(p, now, o.look); this.pose = fr;
    // gx / gy: where a two-person action stands it (public/duo.js), on the ground: the shadow and the depth go along
    const gx = fr.gx ?? 0, gy = fr.gy ?? 0;
    this.body.setDepth(DEPTH.otters + p.drawY + gy + (fr.dz ?? 0)); this.tag.setDepth(DEPTH.tags + p.drawY);
    const sx = x + (fr.ox + gx) * S, sy = y + (fr.oy + gy) * S; // a dive or climb hops up over the edge
    const shade = fr.shadow ?? (fr.wet ? 0 : 1); // fading out / in getting into / out of the water; on the ground, not up with the hop
    this.shadow.setVisible(shade > 0).setAlpha(shade).setPosition((p.drawX + fr.ox + gx + 0.5) * 16, (p.drawY + gy + 0.95) * 16);
    const act = fr.act && o.actSheet?.(); // an act action (敲电脑 ...): its act sheet (deck/pose.js only picks one once it's loaded)
    const duo = fr.duo && o.duoSheet?.(); // a two-person action: the look's duo sheet (until it's built: standing, facing the partner)
    this.sprite.setTexture(duo || act || o.sheet, duo ? fr.frame : act ? fr.frame : fr.act ? fr.stand : fr.duo ? fr.stand ?? 0 : fr.frame).setPosition(wx(Math.round(sx)), wy(Math.round(sy)));
    // the name tag (screen pixels): a filled box with a 2 px ink border centred on its edge, as strokeRect drew it
    toScreen(this.tag, cam);
    const face = o.face, icons = o.icons ?? [], ip = icons.length * TAG_ICON, pad = (face ? 20 : 0) + ip;
    const tw = measure(NAME_FONT, o.label), w = tw + 12 + pad, lx = Math.round(x + 16 * SCALE), ly = Math.round(y - 4 + (p._tagY = fr.tagY ?? 0) * S); // tagY: seated (deck/pose.js)
    const bx = lx - w / 2, by = ly - 14, bh = 18;
    setRect(this.fill, bx + 1, by + 1, w - 2, bh - 2, o.fill, o.alpha);
    const [t, b, l, r] = this.edges;
    setRect(t, bx - 1, by - 1, w + 2, 2, INK); setRect(b, bx - 1, by + bh - 1, w + 2, 2, INK);
    setRect(l, bx - 1, by + 1, 2, bh - 2, INK); setRect(r, bx + w - 1, by + 1, 2, bh - 2, INK);
    if (face?.key) this.face.setTexture(face.key).setDisplaySize(14, 14).setPosition(Math.round(bx + 4), ly - 12).setVisible(true);
    else { this.face.setVisible(false); if (face?.img) o.faces.place(face, Math.round(bx + 4), ly - 12); }
    if (this.label.text !== o.label) this.label.setText(o.label);
    placeText(this.label, lx + pad / 2 - tw / 2, ly, NAME_FONT);
    while (this.tagIcons.length < icons.length) { const img = uiImage(this.scene); this.tag.add(img); this.tagIcons.push(img); }
    const ix = Math.round(lx + pad / 2 - tw / 2) - ip; // the icons sit just before the text
    this.tagIcons.forEach((img, i) => (i < icons.length ? showIcon(img, icons[i], ix + i * TAG_ICON, by + 1) : img.setVisible(false)));
    this.iconHits = icons.map((name, i) => ({ name, x: ix + i * TAG_ICON, y: by + 1, w: 16, h: 16 }));
    const ring = this.ring;
    if (o.speaking) {
      const sx = lx + w / 2 + 4, sy = ly - 5, k = (now / 150) % 3;
      ring.clear().setVisible(true);
      for (let i = 0; i < 3; i++) ring.lineStyle(2, INK, i <= k ? 1 : 0.3).strokePoints(arcPoints(sx, sy, 3 + i * 4, -0.7, 0.7), false, false);
    } else if (ring.visible) ring.clear().setVisible(false);
    // 睡觉 lying down: its icon pops up, then fades after EMOTE_MS and leaves it to the z z (no sleep frames: it stays)
    const em = fr.emote && { ...fr.emote, left: fr.emote.def.sleep && fr.kind === 'sleep' ? EMOTE_MS - fr.emote.t : p.emote ? p.emote.until - now : 0 };
    this.emoteMarks(em, lx, by - 1, y + 8 * SCALE, SCALE, now);
    this.duoMarks(fr.fx, cam, SCALE);
  }
  /** A two-person action's marks over the pair (public/duo.js fx, on the one who asked): hearts rising from between
   *  them (蹭蹭, 一起漂), one heart as the noses touch (碰鼻子, retired), a spark where the paws meet (击掌, 转圈圈). Screen pixels. */
  duoMarks(fx, cam, SCALE) {
    const hearts = fx && this.scene.textures.exists('emotes') && (fx.kind === 'hearts' || fx.kind === 'heart');
    if (!fx || (!hearts && fx.kind !== 'spark')) { this.duoFx?.forEach((h) => h.setVisible(false)); return; }
    if (!this.duoFx) { this.duoFx = [0, 1, 2].map(() => this.scene.add.image(0, 0, '__DEFAULT').setVisible(false)); this.tag.add(this.duoFx); }
    const S = cam.S(), cx = cam.x + fx.x * S, headY = cam.y + fx.y * S + S - 22 * SCALE, fade = Math.max(0, Math.min(1, (fx.T - fx.t) / 300));
    this.duoFx.forEach((h, i) => {
      if (hearts) {
        const n = fx.kind === 'heart' ? 1 : 3, start = fx.kind === 'heart' ? 500 : 300 + i * 420;
        const ph = ((fx.t - start) % 1260) / 1260;
        if (i >= n || fx.t < start || (fx.kind === 'heart' && fx.t > start + 1260)) return h.setVisible(false);
        const x = cx + (n > 1 ? (i - 1) * 6 * SCALE : 0) + Math.sin(ph * 6 + i * 2) * 2 * SCALE, y = headY - 4 * SCALE - ph * 14 * SCALE;
        h.setTexture('emotes', HEART_ICON).setVisible(true).setPosition(Math.round(x), Math.round(y)).setScale(SCALE).setAlpha(Math.min(fade, 1 - ph * ph));
      } else { // a spark: pops over the pair 450 ms in (击掌's slap; 转圈圈 as they start round)
        if (i > 0 || !this.scene.textures.exists(UI)) return h.setVisible(false);
        const at = fx.t - 450, on = at > 0 && at < 600, k = on ? 1 + 0.6 * Math.sin((at / 600) * Math.PI) : 0;
        if (!on) return h.setVisible(false);
        h.setTexture(UI, uiFrame('sparkle')).setOrigin(0.5).setVisible(true).setPosition(Math.round(cx), Math.round(headY - 6 * SCALE)).setScale(k * SCALE * 0.75).setAlpha(Math.min(fade, 1 - at / 700));
      }
    });
  }
}

/** Chat bubbles over everyone who just said something (screen). Each is centred on its sender, just above the
 *  sender's own name; if that spot would cover another bubble or someone else's name, the later message moves up
 *  and a leader line keeps its tail tied to its own sender. */
export class Bubbles {
  constructor(scene) {
    this.scene = scene;
    this.layer = scene.add.container(0, 0).setDepth(DEPTH.bubbles);
    this.leaders = scene.add.graphics(); this.layer.add(this.leaders);
    this.pool = [];
  }
  slot(i) {
    while (this.pool.length <= i) {
      const g = this.scene.add.graphics(), texts = [0, 1, 2].map(() => this.scene.add.text(0, 0, '', textStyle(BUBBLE_FONT, '#1b1410')));
      this.layer.add([g, ...texts]); this.pool.push({ g, texts });
    }
    return this.pool[i];
  }
  update(all, cam) {
    toScreen(this.layer, cam);
    const talking = all.filter((p) => p.say && (Date.now() <= p.say.until || (p.say = null)));
    const S = cam.S(), SCALE = cam.scale;
    const badgeTop = (p) => Math.round(cam.y + (p.drawY + (p._tagY ?? 0)) * S + S - 30 * SCALE - 4 - 14); // see OtterView
    const names = talking.length ? all.map((p) => { // name badges (roughly) are in the way too, except the sender's own
      const w = measure(NAME_FONT, p.name) + 44;
      return { p, x: Math.round(cam.x + (p.drawX + 0.5) * S - w / 2), y: badgeTop(p), w, h: 18 };
    }) : [];
    const placed = [], boxes = [];
    for (const p of talking.sort((a, b) => a.say.at - b.say.at)) { // earlier messages keep their spot
      const lines = p.say.lines || (p.say.lines = bubbleLines(p.say.text));
      const lh = 17, w = Math.ceil(Math.max(...lines.map((l) => measure(BUBBLE_FONT, l)))) + 16, h = lines.length * lh + 8;
      const cx = Math.round(cam.x + (p.drawX + 0.5) * S), anchor = badgeTop(p) - 8 - (p.emote ? EMOTE_ICON_PX + 2 : 0), left = cx - w / 2;
      let bottom = anchor;
      const hits = (r) => left < r.x + r.w + 4 && left + w + 4 > r.x && bottom + 8 > r.y && bottom - h < r.y + r.h + 4;
      for (let moved = true; moved; ) {
        moved = false;
        for (const r of [...names.filter((n) => n.p !== p), ...placed]) if (hits(r)) { bottom = r.y - 10; moved = true; }
      }
      placed.push({ x: left, y: bottom - h, w, h });
      boxes.push({ p, lines, lh, w, h, cx, left, bottom, anchor });
    }
    this.rects = placed; // where the bubbles are this frame (screen px): the engine UI keeps out of their way (deck/duo-view.js)
    const L = this.leaders; L.clear();
    for (const b of boxes) if (b.bottom !== b.anchor) { // leader lines first, so bubbles cover any crossing
      const a = Math.min(1, (b.p.say.until - Date.now()) / 400);
      L.lineStyle(4, 0xffffff, a).lineBetween(b.cx, b.bottom + 4, b.cx, b.anchor + 6);
      L.lineStyle(2, INK, a).lineBetween(b.cx, b.bottom + 4, b.cx, b.anchor + 6);
    }
    const top0 = fontMetrics(BUBBLE_FONT).topToBaseline;
    boxes.forEach((b, i) => {
      const { g, texts } = this.slot(i), top = b.bottom - b.h, a = Math.min(1, (b.p.say.until - Date.now()) / 400);
      g.clear().setVisible(true);
      const outline = roundedRectPoints(b.left, top, b.w, b.h, 8);
      g.fillStyle(0xffffff, a).fillPoints(outline, true, true);
      g.lineStyle(2, INK, a).strokePoints(outline, true, true);
      g.fillTriangle(b.cx - 6, b.bottom - 1, b.cx, b.bottom + 6, b.cx + 6, b.bottom - 1);
      g.beginPath(); g.moveTo(b.cx - 6, b.bottom); g.lineTo(b.cx, b.bottom + 6); g.lineTo(b.cx + 6, b.bottom); g.strokePath();
      texts.forEach((t, k) => {
        const l = b.lines[k];
        if (l == null) { t.setVisible(false); return; }
        if (t.text !== l) t.setText(l);
        t.setVisible(true).setAlpha(a); placeText(t, b.left + 8, top + 5 + k * b.lh + top0, BUBBLE_FONT);
      });
    });
    for (let i = boxes.length; i < this.pool.length; i++) { this.pool[i].g.clear().setVisible(false); this.pool[i].texts.forEach((t) => t.setVisible(false)); }
  }
}

/** 钓鱼 (public/fishing.js keeps the state on each otter: p.fish while a line is out, p.fishPop for a catch): the rod
 *  over the otter, the line, the bobber (flying out on the cast, bobbing, pulled under on a bite, splashing while it's
 *  reeled), the bite's "!" over the angler, and a caught fish jumping from the water to the angler. Pixel art from
 *  /fishing.png and /fish.png (assets/fishing/fishing-art.mjs; deck/fishing-geo.js says where things are). */
const CAST_MS = 450, POP_FLY_MS = 650, POP_MS = 2200;
export class Fishing {
  constructor(scene) {
    this.scene = scene;
    const tex = scene.textures.get('fishing');
    for (let i = 0; i < 12; i++) tex.add('c' + i, 0, i * 16, 0, 16, 16);
    for (let k = 0; k < 8; k++) tex.add('rod' + k, 0, k * FISHING_GEO.rodSize, FISHING_GEO.rodY, FISHING_GEO.rodSize, FISHING_GEO.rodSize);
    this.lines = scene.add.graphics().setDepth(DEPTH.fishing);
    this.items = new Map(); // otter -> {rod, bob, fx, bang, pop}
  }
  item(p) {
    let it = this.items.get(p);
    if (!it) {
      const s = this.scene, img = (key, frame, depth) => s.add.image(0, 0, key, frame).setOrigin(0, 0).setDepth(depth).setVisible(false);
      it = { rod: img('fishing', 'rod0', DEPTH.otters), bob: img('fishing', 'c0', DEPTH.fishing + 0.1), fx: img('fishing', 'c4', DEPTH.fishing + 0.2),
        bang: img('fishing', 'c11', DEPTH.bubbles - 1), pop: img('fish', 0, DEPTH.bubbles - 1), popFx: img('fishing', 'c4', DEPTH.fishing + 0.2) };
      this.items.set(p, it);
    }
    return it;
  }
  /** all: every otter drawn this frame; views: otter -> OtterView (the rod goes exactly where its sprite went) */
  update(now, cam, all, views) {
    const g = this.lines.clear(), seen = new Set(), lw = 1.4 / cam.scale;
    for (const p of all) {
      const f = p.fish, pop = p.fishPop && now - p.fishPop.at < POP_MS ? p.fishPop : null, v = views.get(p);
      if ((!f && !pop) || !v) continue;
      seen.add(p);
      const it = this.item(p), sx = v.sprite.x, sy = v.sprite.y; // the otter's 32x32 frame, world pixels
      it.rod.setVisible(!!f); it.bob.setVisible(!!f); it.fx.setVisible(false); it.bang.setVisible(false);
      if (f) {
        const t = now - f.since, pt = now - f.phaseAt, dir = f.dir & 3;
        const pulling = (f.phase === 'wait' && t < 150) || f.phase === 'bite' || (f.phase === 'reel' && Math.floor(pt / 220) % 2 === 0);
        it.rod.setFrame('rod' + (dir * 2 + (pulling ? 1 : 0))).setPosition(sx - 8, sy - 8)
          .setDepth(DEPTH.otters + p.drawY + (dir === 2 ? -5e-5 : 5e-5));
        const [tx0, ty0] = FISHING_GEO.rodTips[dir][pulling ? 1 : 0], tx = sx - 8 + tx0 + 0.5, ty = sy - 8 + ty0 + 0.5;
        const bx = (f.bx + 0.5) * 16, by = (f.by + 0.5) * 16; // where the bobber floats (its antenna top is 8.5, 3.5 in its cell)
        let cx = bx, cy = by, cell = FISHING_GEO.float[Math.floor(now / 500) % 2];
        if (f.phase === 'wait' && t < CAST_MS) { // flying out
          const k = t / CAST_MS; cx = tx + (bx - tx) * k; cy = ty + (by - ty) * k - Math.sin(Math.PI * k) * 18;
        } else if (f.phase === 'wait') {
          const land = t - CAST_MS;
          if (land < 400) it.fx.setFrame('c' + FISHING_GEO.splash[Math.min(3, Math.floor(land / 100))]).setVisible(true).setAlpha(1).setDepth(DEPTH.fishing + 0.2);
          else it.fx.setFrame('c' + FISHING_GEO.ripple[Math.floor(now / 600) % 3]).setVisible(true).setAlpha(0.55).setDepth(DEPTH.fishing + 0.05); // rings under the bobber
        } else if (f.phase === 'bite') {
          cell = FISHING_GEO.sink[Math.floor(now / 120) % 2];
          it.fx.setFrame('c' + FISHING_GEO.splash[Math.floor(now / 90) % 4]).setVisible(true).setAlpha(1).setDepth(DEPTH.fishing + 0.2);
          it.bang.setVisible(true).setPosition(sx + 8, sy - 26 / cam.scale - 14 - Math.abs(Math.sin(now / 90)) * 3);
        } else if (f.phase === 'reel') {
          cell = FISHING_GEO.sink[Math.floor(now / 200) % 2];
          cx += Math.round(Math.sin(now / 70)); // the fish tugging
          it.fx.setFrame('c' + FISHING_GEO.splash[1 + (Math.floor(now / 140) % 3)]).setVisible(true).setAlpha(1).setDepth(DEPTH.fishing + 0.2);
        }
        if (it.fx.visible) it.fx.setPosition(Math.round(bx - 8.5), Math.round(by - 9));
        it.bob.setFrame('c' + cell).setPosition(Math.round(cx - 8.5), Math.round(cy - 8));
        // the line: from the rod tip to the bobber's antenna, sagging while it waits, taut once a fish is on
        const ex = Math.round(cx - 8.5) + 8.5, ey = Math.round(cy - 8) + FISHING_GEO.antenna[cell];
        const sag = f.phase === 'wait' && t >= CAST_MS ? 7 : 0, mx = (tx + ex) / 2, my = (ty + ey) / 2 + sag, pts = [];
        for (let i = 0; i <= 12; i++) { const k = i / 12, u = 1 - k; pts.push({ x: u * u * tx + 2 * u * k * mx + k * k * ex, y: u * u * ty + 2 * u * k * my + k * k * ey }); }
        g.lineStyle(lw, 0xffffff, 0.9).strokePoints(pts, false, false);
      }
      // a catch: the fish jumps out where the bobber was and lands beside the angler's head (above it is the chat bubble), then fades
      it.pop.setVisible(!!pop); it.popFx.setVisible(false);
      if (pop) {
        const t = now - pop.at, hx = sx + 25, hy = sy + 4, k = Math.min(1, t / POP_FLY_MS);
        const fx = pop.bx * 16, fy = pop.by * 16, x = fx + (hx - fx) * k, y = fy + (hy - fy) * k - Math.sin(Math.PI * k) * 20 + (k === 1 ? Math.sin(t / 150) * 1.5 : 0);
        it.pop.setFrame(pop.i).setPosition(Math.round(x), Math.round(y)).setAlpha(Math.min(1, (POP_MS - t) / 300));
        if (t < 400) it.popFx.setFrame('c' + FISHING_GEO.splash[Math.floor(t / 100)]).setPosition(Math.round(fx), Math.round(fy - 1)).setVisible(true);
      }
    }
    for (const [p, it] of this.items) if (!seen.has(p)) { for (const o of Object.values(it)) o.destroy(); this.items.delete(p); }
  }
}
